<template>
  <div
    style="width: 100%"
    class="q-mx-auto"
    :style="{ 'max-width': $isLandscapeView() && $q.screen.gt.md ? '600px' : undefined }"
  >
    <!-- Mobile stepper -->
    <div
      v-if="$q.screen.xs"
      class="row justify-between items-center"
    >
      <div
        class="col q-px-md"
        :class="$isColorDark(currentSite.headerColor) && $q.dark.isActive ? 'text-white' : 'text-dark'"
      >
        <i18n-t
          keypath="checkout.stepNumberOfTotal"
          tag="div"
          scope="global"
        >
          <template #step>
            <b>{{ stepIndex }}</b>
          </template>
          <template #total>
            <b>{{ steps.length }}</b>
          </template>
        </i18n-t>
      </div>
      <div
        class="col text-body1 text-weight-bolder q-px-md q-py-xs text-right"
        :class="mobileTextColor"
      >
        {{ currentStepName }}
      </div>
    </div>
    <!-- Desktop stepper -->
    <q-stepper
      v-else
      ref="stepper"
      v-model="stepIndex"
      class="bg-transparent"
      :class="$q.screen.xs ? 'dense' : 'dense-md'"
      :style="{ 'max-height': $q.screen.xs ? '34px' : $isLandscapeView() ? '80px' : '55px' }"
      :header-class="$q.screen.xs ? 'small-icon' : !$isLandscapeView() ? 'justify-between' : undefined"
      :alternative-labels="$q.screen.gt.xs && $isLandscapeView()"
      flat
      animated
      :ripple="false"
      header-nav
    >
      <q-step
        v-for="(step, i) in steps"
        :key="`step=${i}`"
        class="text-weight-bold"
        :name="`${i + 1}`"
        :title="step.name"
        :icon="`fal fa-${i + 1}`"
        :color="stepIndex === i + 1? 'primary' : 'responsive-grey-7'"
        :disable="isStepNavigationDisabled(step, i)"
        :done="stepIndex > i + 1"
      />
    </q-stepper>
    <!-- Mobile progress bar -->
    <q-linear-progress
      v-if="$q.screen.xs || !$isLandscapeView()"
      :value="progress"
      :color="progressColor"
    />
  </div>
</template>

<script>
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import { useHasPurchasableFood } from 'src/modules/checkout/composables/sales-availability/hasPurchasableFood'
import { useSalesChannel } from 'src/modules/checkout/composables/sales-channels/SalesChannel'

export default {
  name: 'CheckoutStepper',
  context: ['currentUser'],
  configuration: ['currentSite', 'categories', 'items'],
  inject: ['order', 'checkoutData'],
  setup () {
    const { hasPurchasableFood } = useHasPurchasableFood()
    const { computeSalesChannel } = useSalesChannel()

    return {
      hasPurchasableFood,
      computeSalesChannel
    }
  },
  data () {
    return {
      stepIndex: 1
    }
  },
  computed: {
    salesChannel () {
      return this.computeSalesChannel(SALES_CHANNEL_TYPES.MENU, this.order)
    },
    // Determines whether or not food can be purchased during checkout
    canPurchaseFood () {
      return !this.isDigitalSaleOnly &&
        !this.purchasingMembership &&
        this.hasPurchasableFood({
          categories: this.categories.data,
          items: this.items.data,
          salesChannel: this.salesChannel,
          order: this.order,
          // We can't use context directly in case we are on Consumer, which doesn't have a register client
          registerType: this.order?.context?.currentRegisterClient?.registerStation?.registerType,
          showing: this.order?.currentShowing || this.order?.nextShowing
        })
    },
    currentStepName () {
      if (!this.steps[this.stepIndex - 1]) return ''

      return this.steps[`${this.stepIndex - 1}`].name
    },
    // This flag indicates if the order only includes digital items like vouchers
    isDigitalSaleOnly () {
      return (this.order?.purchasingGiftCards || this.order?.purchasingVouchers || this.order?.purchasingDonation || this.purchasingMembership) &&
        !(this.order?.purchasingTickets || this.order?.purchasingItems)
    },
    showDeliveryStep () {
      if (!this.$kiosk) return false

      if (this.order.purchasingShowing) return false

      if (!this.order.purchasingItems) return false

      const deliveryType = this.order?.deliveryType

      return deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT || (deliveryType === DELIVERY_TYPE_OPTIONS.COUNTER_PICKUP && this.currentSite.useNameForCounterPickup) ||
      (deliveryType === DELIVERY_TYPE_OPTIONS.EXPRESS_PICKUP && this.currentSite.useNameForExpressPickup)
    },
    isMobileView () {
      return (this.$q.screen.xs || !this.$isLandscapeView())
    },
    showSeatChart () {
      return (this.currentUser && this.currentUser.isRemote) || this.checkoutData.guestCheckoutConfirmed
    },
    // Determines the checkout flow steps
    steps () {
      const steps = []

      if (this.purchasingMembership) {
        // Membership checkout step(s)
        if (this.currentUser && this.currentUser.isRemote) {
          steps.push({ name: this.$i18n.t('checkout.steps.membership'), route: '/checkout/modify-membership' })
        } else {
          steps.push({ name: this.$i18n.t('checkout.steps.logIn'), route: '/register' })
        }
      } else if (!_.isNil(this.order?.currentShowing)) {
        if (this.currentSite.consumerConfig.showTicketFirstCheckoutFlow) {
          // Ticket checkout step(s)
          // Step: Ticket selection
          steps.push({ name: this.$i18n.t('checkout.steps.tickets'), route: this.order?.currentShowing?.checkoutPath })
          if (this.order?.currentShowing?.reservedSeating) {
          // Step: Seat selection
            steps.push({ name: this.$i18n.t('checkout.steps.seats'), route: `/checkout/seats/${this.order?.currentShowing?.id}` })
          }
        } else {
          steps.push({
            name: this.order.currentShowing.reservedSeating
              ? this.showSeatChart
                ? this.$i18n.t('checkout.steps.seats')
                : this.$i18n.tc('user.account', 1)
              : this.$i18n.t('checkout.steps.tickets'),
            route: this.order.currentShowing.checkoutPath
          })
        }
        if (this.currentSite.checkoutOptions.show_more_films_during_checkout) {
          // Step: "More films" which prompts users to purchase tickets for additional showings
          steps.push({ name: this.$i18n.t('checkout.steps.moreFilms'), route: '/checkout/more-films' })
        }
      } else if (!_.isNil(this.order?.currentActivity)) {
        // Activity checkout step(s)
        steps.push({ name: this.order.currentActivity.name, route: `/checkout/activity/${this.order.currentActivity.urlSlug}` })
        steps.push({ name: this.$i18n.t('checkout.steps.dateAndTime'), route: `/checkout/activity/${this.order.currentActivity.urlSlug}/date-and-time` })

        if (!_.isEmpty(this.order.currentActivity.activityCategories.data)) {
          steps.push({ name: this.$i18n.t('checkout.steps.addOns'), route: `/checkout/activity/${this.order.currentActivity.urlSlug}/add-ons` })
        }

        if (this.currentSite.checkoutOptions.show_more_activities_during_checkout) {
          steps.push({ name: this.$i18n.t('checkout.steps.moreActivities'), route: '/checkout/more-activities' })
        }
      }

      if (this.order?.hasTicketPackageWithRequiredModifierSets) {
        // Step: Ticket package options
        steps.push({ name: this.$i18n.t('checkout.steps.options'), route: '/checkout/ticket-package-options' })
      }

      // Step: Food and drink selection
      if (this.canPurchaseFood) {
        steps.push({ name: this.$i18n.t('checkout.steps.foodAndDrink'), route: '/checkout/items' })
      }

      if (this.showDeliveryStep) {
        steps.push({ name: this.$i18n.t('checkout.steps.delivery'), route: '/checkout/delivery-selection' })
      }

      if (this.isMobileView && !this.isDigitalSaleOnly && !this.checkoutData.guestCheckoutConfirmed) {
        // On mobile, we show a 'Redemptions' step if redemptions are available
        if (this.checkoutData.hasRedemptions) {
          steps.push({ name: this.$i18n.t('checkout.steps.redemptions'), route: '/checkout/cart' })
        }
      } else if (!this.isMobileView) {
        // On desktop, we always show the 'Cart' step
        steps.push({ name: this.$i18n.t('checkout.steps.cart'), route: '/checkout/cart' })
      }

      // Step: payment
      steps.push({ name: this.$i18n.t('checkout.steps.payment'), route: '/checkout/payment' })

      return steps
    },
    // Create a slug of the checkout steps for tracking purposes.
    // Example: 'tickets,seats,food-and-drink,cart,payment'
    stepsSlug () {
      return _.map(this.steps, 'name').map(name => name.toLowerCase().replace(/\s+/g, '-')).join(',')
    },
    progress () {
      return this.stepIndex / this.steps.length
    },
    purchasingMembership () {
      return this.checkoutData?.purchasingMembership || this.order?.purchasingMembership
    },
    progressColor () {
      if (this.$q.dark.isActive && this.$q.screen.xs) return this.$isColorDark(this.currentSite.headerColor) ? 'white' : 'black'
      return 'primary'
    },
    mobileTextColor () {
      if (this.$q.dark.isActive) return this.$isColorDark(this.currentSite.headerColor) ? 'text-white' : 'text-dark'
      return 'text-primary'
    },
    currentRoute () {
      return this.$router.currentRoute.value
    },
    hasActivityBookingWithMinimumAttendanceRequirementsMet () {
      if (!this.order.currentActivity) return false

      const activityBookingIdFromCurrentRouteQuery = this.currentRoute.query.activityBookingId

      let existingActivityBooking

      if (!_.isNil(activityBookingIdFromCurrentRouteQuery)) {
        existingActivityBooking = this.order.getActivityBookingByActivityBookingId(this.order.currentActivity, activityBookingIdFromCurrentRouteQuery)
      } else {
        existingActivityBooking = this.order.getActivityBookingForActivity(this.order.currentActivity)
      }

      return existingActivityBooking?.areMinimumAttendanceRequirementsMet || false
    }
  },
  watch: {
    'checkoutData.stepTitle': {
      handler () {
        this.stepIndex = _.indexOf(_.map(this.steps, 'name'), this.checkoutData.stepTitle) + 1
        this.checkoutData.nextButtonLabel = this.$i18n.t('checkout.actions.nextStep', { step: this.steps[this.stepIndex]?.name })
      },
      immediate: true
    },
    'steps' () {
      this.stepIndex = _.indexOf(_.map(this.steps, 'name'), this.checkoutData.stepTitle) + 1
      this.checkoutData.nextButtonLabel = this.$i18n.t('checkout.actions.nextStep', { step: this.steps[this.stepIndex]?.name })
      this.checkoutData.trackingData = { checkoutStepsSlug: this.stepsSlug }
    },
    'stepIndex' () {
      if (!this.steps[this.stepIndex - 1]) return

      // This is where the checkout flow routing happens
      this.$router.push({ path: this.steps[this.stepIndex - 1].route, query: this.$route.query })
    }
  },
  methods: {
    isStepNavigationDisabled (step, i) {
      const isModifyingMembership = this.purchasingMembership && this.stepIndex === 1 && i === 1
      const isSeatsStepForOrderWithoutTickets = step.name === this.$i18n.t('checkout.steps.seats') && this.order.showingTicketOrderItems(this.order.currentShowing).length === 0
      const isDisabledActivityCheckoutStep = (step.name === this.$i18n.t('checkout.steps.dateAndTime') || step.name === this.$i18n.t('checkout.steps.addOns')) && !this.hasActivityBookingWithMinimumAttendanceRequirementsMet
      const isCartOrPaymentsStepForOrderWithoutItems = this.order.draftOrderItemsCartCount === 0 && i >= (this.steps.length - 2)
      const isFoodOrCartOrPaymentsStepAndOrderNeedsSeatSelection = !_.isNil(this.order.firstOrderItemNeedingSeatSelection()) && (step.name === this.$i18n.t('checkout.steps.payment') || step.name === this.$i18n.t('checkout.steps.cart') || step.name === this.$i18n.t('checkout.steps.foodAndDrink'))
      return isModifyingMembership || isSeatsStepForOrderWithoutTickets || isDisabledActivityCheckoutStep || isCartOrPaymentsStepForOrderWithoutItems || isFoodOrCartOrPaymentsStepAndOrderNeedsSeatSelection
    },
    next () {
      if (this.stepIndex < this.steps.length) this.stepIndex++
    },
    back () {
      if (this.stepIndex > 1) this.stepIndex--
    }
  }
}
</script>

<style lang="scss">
.dense .q-stepper__tab {
  padding: 8px 2px;
  margin-top: 0;
  font-weight: bold;
  min-height: unset;
}
.dense-md .q-stepper__tab {
  padding: 16px 8px;
  margin-top: 0;
  font-weight: bold;
  min-height: unset;
}
.small-icon .q-stepper__dot {
  width: 15px;
  min-width: 15px;
  height: 15px;
  font-size: 10px;
}
.small-icon .q-stepper__tab--done .q-stepper__dot {
  display: block;
}

.small-icon .q-stepper__tab--done .q-stepper__dot .material-icons{
  font-size: 12px;
  padding: 2px;
}
.small-icon .q-stepper__line:after, .small-icon .q-stepper__line:before {
   display: none;
}
</style>
